/**
 * Custom hook for Data Visualization module
 * Handles DuckDB-WASM initialization, Supabase data loading, and schema discovery
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initDuckDB } from '../sql/editor/SQLEditor';
import { supabase } from '../lib/supabase';
import { getExerciseDatasets } from '../components/exercises/config/datasetConfig';

// Helper function to format display values (especially Unix timestamps)
// Matches the same logic used in SQLEditor for consistent date formatting
function formatDisplayValue(val) {
    if (val === null || val === undefined) return null;
    // Handle HUGEINT: DuckDB-WASM may return Int128 as {lo, hi} object
    if (val !== null && typeof val === 'object' && ('lo' in val || 'high' in val || 'low' in val)) {
        const lo = Number('lo' in val ? val.lo : (val.low ?? 0));
        const hi = Number('high' in val ? val.high : (val.hi ?? 0));
        // For typical aggregation results (< 2^53), hi === 0
        return hi === 0 ? lo : hi * Math.pow(2, 32) * Math.pow(2, 32) + lo;
    }
    // Handle JS Date objects (DuckDB DATE columns come back as Date via Arrow toJSON)
    if (val instanceof Date) {
        return val.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }
    let numVal = typeof val === 'bigint' ? Number(val) : val;
    if (typeof numVal === 'string' && !isNaN(numVal) && numVal.trim() !== '') numVal = Number(numVal);
    // Detect Unix timestamp (seconds or milliseconds)
    if (typeof numVal === 'number' && numVal > 1000000000 && numVal < 10000000000000) {
        const timestamp = numVal > 10000000000 ? numVal : numVal * 1000;
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return typeof val === 'bigint' ? Number(val) : numVal;
}

// IndexedDB cache helpers (reuse same cache as SQLEditor)
const CACHE_DB_NAME = 'duckdb_dataset_cache';
const CACHE_STORE_NAME = 'datasets';

async function openCacheDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(CACHE_DB_NAME, 1);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
                db.createObjectStore(CACHE_STORE_NAME, { keyPath: 'key' });
            }
        };
    });
}

async function getCachedData(key) {
    const cacheDB = await openCacheDB();
    return new Promise((resolve, reject) => {
        const transaction = cacheDB.transaction([CACHE_STORE_NAME], 'readonly');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const request = store.get(key);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result?.data);
    });
}

async function setCachedData(key, data) {
    const cacheDB = await openCacheDB();
    return new Promise((resolve, reject) => {
        const transaction = cacheDB.transaction([CACHE_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(CACHE_STORE_NAME);
        const request = store.put({ key, data, timestamp: Date.now() });
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
    });
}

export function useDataViz(moduleId = 'DATA_VIZ_1') {
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState('');
    const [schemas, setSchemas] = useState({}); // { tableName: [{ name, type }] }
    const connRef = useRef(null);
    const dbRef = useRef(null);
    const loadedRef = useRef(false);

    const datasets = useMemo(() => getExerciseDatasets(moduleId), [moduleId]);

    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;

        const load = async () => {
            try {
                setLoadingProgress('Initializing database...');
                const { db, conn } = await initDuckDB();
                dbRef.current = db;
                connRef.current = conn;

                const datasetFiles = datasets.datasetFiles;

                for (const dataset of datasetFiles) {
                    const tableName = dataset.tableAlias || dataset.name.replace(/\.csv$/, '');
                    const bucketPrefix = dataset.bucket.replace(/[^a-zA-Z0-9_]/g, '_');
                    const baseName = dataset.name.replace(/\.(csv|parquet|json)$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
                    const internalName = `${bucketPrefix}_${baseName}`;
                    const cacheKey = `${dataset.bucket}/${dataset.name}`;

                    setLoadingProgress(`Loading ${tableName}...`);

                    // Check if internal table already exists
                    try {
                        const existing = await conn.query(`SELECT * FROM ${internalName} LIMIT 1`);
                        if (existing && existing.numRows !== undefined) {
                            await conn.query(`CREATE OR REPLACE VIEW ${tableName} AS SELECT * FROM ${internalName}`);

                            // Fetch schema
                            const schemaResult = await conn.query(`DESCRIBE ${tableName}`);
                            const cols = schemaResult.toArray().map(r => {
                                const row = r.toJSON ? r.toJSON() : r;
                                return { name: row.column_name, type: row.column_type };
                            });
                            setSchemas(prev => ({ ...prev, [tableName]: cols }));
                            continue;
                        }
                    } catch (e) {
                        // Table doesn't exist, proceed to load
                    }

                    // Try cache first
                    let fileBuffer;
                    const cachedBlob = await getCachedData(cacheKey).catch(() => null);

                    if (cachedBlob) {
                        fileBuffer = await cachedBlob.arrayBuffer();
                    } else {
                        const { data } = supabase.storage.from(dataset.bucket).getPublicUrl(dataset.name);
                        if (!data?.publicUrl) throw new Error(`Failed to get URL for ${dataset.name}`);

                        const response = await fetch(data.publicUrl);
                        if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);

                        const blob = await response.blob();
                        await setCachedData(cacheKey, blob);
                        fileBuffer = await blob.arrayBuffer();
                    }

                    await db.registerFileBuffer(dataset.name, new Uint8Array(fileBuffer));
                    await conn.query(`CREATE OR REPLACE TABLE ${internalName} AS SELECT * FROM read_csv_auto('${dataset.name}')`);
                    await conn.query(`CREATE OR REPLACE VIEW ${tableName} AS SELECT * FROM ${internalName}`);

                    // Fetch schema with types
                    const schemaResult = await conn.query(`DESCRIBE ${tableName}`);
                    const cols = schemaResult.toArray().map(r => {
                        const row = r.toJSON ? r.toJSON() : r;
                        return { name: row.column_name, type: row.column_type };
                    });
                    setSchemas(prev => ({ ...prev, [tableName]: cols }));
                }

                setLoadingProgress('');
                setIsReady(true);
            } catch (err) {
                console.error('Data viz load error:', err);
                setError(err.message);
                setLoadingProgress('');
            }
        };

        load();
    }, [datasets]);

    const executeQuery = useCallback(async (sql) => {
        if (!connRef.current) throw new Error('Database not ready');
        const start = performance.now();
        const result = await connRef.current.query(sql);
        const elapsed = ((performance.now() - start) / 1000).toFixed(3);
        const rows = result.toArray().map(r => {
            const row = r.toJSON ? r.toJSON() : r;
            // Format all values - converts BigInts and Unix timestamps to readable formats
            const cleaned = {};
            for (const [k, v] of Object.entries(row)) {
                cleaned[k] = formatDisplayValue(v);
            }
            return cleaned;
        });
        return { rows, executionTime: elapsed, rowCount: rows.length };
    }, []);

    return { isReady, error, loadingProgress, schemas, executeQuery };
}
